<template>
  <div class="view-offers-price">
    <app-content-title
      title="Ценовая политика предложения"
      :link-to="backRoute"
      link-text="Вернуться к предложению"
    >
    </app-content-title>

    <div v-if="!isLoading" class="view-offers-price__price">
      <div class="view-offers-price__price-title row">
        <div class="col-md-12 col-6">
          <h2 class="view-offers-price__price-heading">{{ title }}</h2>
        </div>
        <div class="col-md-12 col-6 view-offers-price__row-select">
          <div class="view-offers-price__col-select">
            <app-select-control
              title="Выберите период"
              label="label"
              track-by="label"
              is-tooltip
              tooltip="Выберите период"
              v-model="dateOption"
              :options="$options.OPTIONS.DATE"
              class="view-offers-price__price-select"
              :allowEmpty="false"
            />
          </div>
          <div class="view-offers-price__col-select">
            <app-select-control
              title="Выберите тип объема"
              label="label"
              track-by="label"
              is-tooltip
              tooltip="Выберите тип объемы"
              v-model="volumeOption"
              :options="$options.OPTIONS.VOLUME"
              :allowEmpty="false"
              class="view-offers-price__price-select"
            />
          </div>
        </div>
      </div>
      <app-chart-line
        class="view-offers-price__chart"
        :chartData="{
          labels: labels,
          datasets: [
            {
              label: shortenedTitle,
              borderColor: '#02B55F',
              backgroundColor: 'transparent',
              cubicInterpolationMode: 'monotone',
              data: dataChart,
              pointBorderWidth: '4px',
              pointRadius: 4,
              pointBackgroundColor: '#02B55F',
            },
          ],
        }"
        :options="{
          responsive: true,
          maintainAspectRatio: false,
          legend: { display: false },
          aspectRatio: 1,
          scales: {
            yAxes: [
              {
                ticks: {
                  beginAtZero: false,
                  callback: (value) => this.$options.filters.price(value),
                },
              },
            ],
          },
        }"
      ></app-chart-line>
    </div>

    <app-loader v-if="isLoading" isCircle isAbsolute></app-loader>
  </div>
</template>

<script>
import { offersMapper } from '@/store/offers';
import {
  getMonth,
  lastDayOfMonth,
  startOfWeek,
  lastDayOfWeek,
  startOfQuarter,
  lastDayOfQuarter,
  subDays,
} from 'date-fns';

export default {
  name: 'ViewOffersPrice',
  components: {
    AppContentTitle: () => import('@/components/common/AppContentTitle'),
    AppLoader: () => import('@/components/ui/AppLoader'),
    AppSelectControl: () => import('@/components/ui/AppSelectControl'),
    AppChartLine: () => import('@/components/charts/AppChartLine'),
  },
  data: () => ({
    isLoading: false,
    priceHistoryParams: {
      from: '',
      to: '',
      unit: 'tn',
    },
    volumeOption: { label: 'Тонны', unit: 'tn' },
    dateOption: { label: 'Год', num: 3 },
    response: [],
    labels: [],
    dataChart: [],
    year: '',
    offerData: null,
  }),
  computed: {
    ...offersMapper.mapState(['categoriesList']),
    /**
     * id текущей страницы
     */
    id() {
      return +this.$route.params.id;
    },
    /**
     * Роут предидущей страницы
     */
    backRoute() {
      return `/offers/item/${this.id}`;
    },
    /**
     * Заголовок
     */
    title() {
      if (this.offerData && this.offerData.fullName) {
        return this.offerData.fullName;
      }
      if (this.offerData && this.offerData.name) {
        return this.offerData.name;
      }
      return '';
    },
    shortenedTitle() {
      if (this.offerData && this.offerData.name) {
        return this.offerData.name;
      }
      if (this.offerData && this.offerData.fullName) {
        return this.offerData.fullName;
      }
      return '';
    },

    unit() {
      switch (this.offerData.priceType) {
        case 'perPiece':
          return 'pc';
        case 'perLitter':
          return 'lt';
        default:
          return 'tn';
      }
    },
  },
  watch: {
    volumeOption(value) {
      this.priceHistoryParams.unit = value.unit;
      this.setDataChart();
    },
    dateOption(value) {
      if (value.num === 0) {
        this.setPriceHistoryForLastDays(7);
      }
      if (value.num === 1) {
        this.setPriceHistoryForLastDays(30);
      }
      if (value.num === 2) {
        this.setPriceHistoryForLastDays(90);
      }
      if (value.num === 3) {
        this.setPriceHistoryForLastDays(365);
      }
      if (value.num === 4) {
        this.setPriceHistoryForLastDays(99999);
      }
    },
  },
  async created() {
    this.$options.OPTIONS = {
      DATE: [
        { label: 'Неделя', num: 0 },
        { label: 'Месяц', num: 1 },
        { label: 'Квартал', num: 2 },
        { label: 'Год', num: 3 },
        { label: 'Все время', num: 4 },
      ],
      VOLUME: [
        { label: 'Тонны', unit: 'tn' },
        { label: 'Литры', unit: 'lt' },
        { label: 'Штуки', unit: 'pc' },
      ],
    };
    this.year = new Date().getFullYear();
    this.setPriceHistoryForLastDays(365, true);
    await this.asyncData();
  },
  methods: {
    ...offersMapper.mapActions(['getPriceHistory', 'getOffersItem']),
    /**
     * Установка данных за последние дни
     * @param {Number} days за сколько дней
     * @param {Boolean} isStart - указывает нужно ли получать данные по графику
     */
    setPriceHistoryForLastDays(days = 365, isStart = false) {
      this.priceHistoryParams.from = this.$options.filters.dateFormat(
        subDays(new Date(), days),
        'yyyy-MM-dd'
      );
      this.priceHistoryParams.to = this.$options.filters.dateFormat(
        new Date(),
        'yyyy-MM-dd'
      );
      if (!isStart) {
        this.setDataChart();
      }
    },
    /**
     * Установка данных за неделю
     * @param {Boolean} isStart - указывает нужно ли получать данные по графику
     */
    setPriceHistoryWeek(isStart = false) {
      this.priceHistoryParams.from = this.$options.filters.dateFormat(
        startOfWeek(new Date(), { weekStartsOn: 1 }),
        'yyyy-MM-dd'
      );
      this.priceHistoryParams.to = this.$options.filters.dateFormat(
        lastDayOfWeek(new Date(), { weekStartsOn: 1 }),
        'yyyy-MM-dd'
      );
      if (!isStart) {
        this.setDataChart();
      }
    },
    /**
     * Устанвока данных за месяц
     * @param {Boolean} isStart - указывает нужно ли получать данные по графику
     */
    setPriceHistoryMonth(isStart = false) {
      this.priceHistoryParams.from = `${this.year}-${
        getMonth(new Date()) + 1
      }-01`;
      this.priceHistoryParams.to = this.$options.filters.dateFormat(
        lastDayOfMonth(new Date()),
        'yyyy-MM-dd'
      );
      if (!isStart) {
        this.setDataChart();
      }
    },
    /**
     * Устанвока данных за квартал
     * @param {Boolean} isStart - указывает нужно ли получать данные по графику
     */
    setPriceHistoryQuarter(isStart = false) {
      this.priceHistoryParams.from = this.$options.filters.dateFormat(
        startOfQuarter(new Date()),
        'yyyy-MM-dd'
      );
      this.priceHistoryParams.to = this.$options.filters.dateFormat(
        lastDayOfQuarter(new Date()),
        'yyyy-MM-dd'
      );
      if (!isStart) {
        this.setDataChart();
      }
    },
    /**
     * Устанвока данных за год
     * @param {Boolean} isStart - указывает нужно ли получать данные по графику
     */
    setPriceHistoryYear(isStart = false) {
      this.priceHistoryParams.from = `${this.year}-01-01`;
      this.priceHistoryParams.to = `${this.year}-12-31`;
      if (!isStart) {
        this.setDataChart();
      }
    },
    /**
     * Стартовые данные
     */
    async asyncData() {
      try {
        this.isLoading = true;
        await this.getOffer();
        await this.setDataChart();
      } catch {
        // -
      } finally {
        this.isLoading = false;
      }
    },
    /**
     * Установка данных по графику
     */
    async setDataChart() {
      this.response = await this.getPriceHistory({
        id: this.id,
        params: this.priceHistoryParams,
      });

      this.labels = [];
      this.dataChart = [];
      // const filtered = this.response
      //   .reverse()
      //   .filter(
      //     (thing, index, self) =>
      //       index === self.findIndex((t) => t.date === thing.date)
      //   )
      //   .reverse();

      // console.log('filtered', filtered, this.response);
      // filtered.forEach((el) => {
      //   this.labels.push(this.$options.filters.date(el.date, 'date', 'year'));
      //   this.dataChart.push(el.price);
      // });
      this.response.forEach((el) => {
        this.labels.push(this.$options.filters.date(el.date, 'date', 'year'));
        this.dataChart.push(el.price);
      });
    },
    /**
     * Получаем данные по заказу
     */
    async getOffer() {
      this.offerData = await this.getOffersItem({ id: this.id });
      this.priceHistoryParams.unit = this.unit;
      this.volumeOption = this.$options.OPTIONS.VOLUME.find(
        (el) => el.unit === this.unit
      );
    },
  },
};
</script>

<style lang="scss">
.view-offers-price {
  &__price {
    padding: 30px;
    background: $white;
    border-radius: 2px;
    box-shadow: $box-shadow-card-1;
  }

  &__price-select {
    width: 100%;
  }

  &__price-chart {
    height: 500px;
  }

  &__row-select {
    @include grid-in(24px);

    justify-content: flex-end;
    padding-right: 30px;

    @include media-md() {
      justify-content: flex-start;
      padding-right: 15px;
    }

    @include media-xxs() {
      margin: 0;
    }
  }

  &__col-select {
    @include col(6, 12px);
    @include col-xxs(12, 0);

    max-width: 270px;

    @include media-xxs() {
      max-width: 100%;
      margin-bottom: 15px;
    }
  }

  &__price-heading {
    margin-top: 0;
  }

  &__chart {
    position: relative;
    height: 500px;
    margin-top: 25px;

    @include media-xs() {
      height: 400px;
    }

    @include media-xxs() {
      height: 320px;
    }
  }
}
</style>
